import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { activateMarketplaceSubscription } from '../services/marketplaceService';
import { useAuth } from '../hooks/useAuth';

interface MarketplaceSetupPageProps {
  navigateTo: (page: string) => void;
  marketplaceToken: string;
}

const MarketplaceSetupPage: React.FC<MarketplaceSetupPageProps> = ({
  navigateTo,
  marketplaceToken,
}) => {
  const { isAuthenticated, login } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'login_required'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [planId, setPlanId] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      setStatus('login_required');
      return;
    }

    const activate = async () => {
      setStatus('loading');
      const result = await activateMarketplaceSubscription(marketplaceToken);

      if (result.success) {
        setPlanId(result.plan_id || '');
        setStatus('success');
        // Auto-navigate to chat after short delay
        setTimeout(() => navigateTo('chat'), 2000);
      } else {
        setErrorMessage(result.error || 'Activation failed');
        setStatus('error');
      }
    };

    activate();
  }, [isAuthenticated, marketplaceToken, navigateTo]);

  const handleLogin = async () => {
    try {
      await login();
    } catch {
      setErrorMessage('Login failed. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-6">
          Marketplace Subscription Setup
        </h1>

        {status === 'loading' && (
          <div>
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Activating your subscription...</p>
          </div>
        )}

        {status === 'login_required' && (
          <div>
            <p className="text-gray-600 mb-6">
              Please sign in with your organization account to complete the setup.
            </p>
            <button
              onClick={handleLogin}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Sign In
            </button>
          </div>
        )}

        {status === 'success' && (
          <div>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-green-800 mb-2">
              Subscription Activated
            </h2>
            <p className="text-gray-600 mb-4">
              {planId && `Your ${planId.charAt(0).toUpperCase() + planId.slice(1)} plan is now active. `}
              Redirecting to the app...
            </p>
          </div>
        )}

        {status === 'error' && (
          <div>
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-red-800 mb-2">
              Activation Failed
            </h2>
            <p className="text-gray-600 mb-4">{errorMessage}</p>
            <button
              onClick={() => navigateTo('chat')}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Continue to App
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketplaceSetupPage;
